import { type ReactNode, Suspense } from "react";
import { cx, Icon, Spinner, Tooltip } from "../../../ui";
import { s } from "../strings";
import styles from "./Layout.module.css";

/** A section: 16px/500 heading outside one white 12px-radius card (settings 2, live). */
export function Section({ title, children, id }: { title: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className={styles.section} aria-labelledby={id}>
      <h2 className={styles.heading} id={id}>
        {title}
      </h2>
      <div className={styles.card}>{children}</div>
    </section>
  );
}

export interface RowProps {
  title: ReactNode;
  description?: ReactNode;
  control?: ReactNode;
  /** Content under the description (sub-options, value lines). */
  children?: ReactNode;
  info?: string;
  disabled?: boolean;
  /** Indented dependent row inside the same card. */
  nested?: boolean;
}

export function Row({ title, description, control, children, info, disabled, nested }: RowProps) {
  return (
    <div className={cx(styles.row, nested && styles.nested, disabled && styles.disabled)}>
      <div className={styles.text}>
        <div className={styles.title}>
          {title}
          {info && (
            <Tooltip content={info}>
              <button type="button" className={styles.info} aria-label={s.info}>
                <Icon name="info" size={16} />
              </button>
            </Tooltip>
          )}
        </div>
        {description && <div className={styles.description}>{description}</div>}
        {children}
      </div>
      {control !== undefined && <div className={styles.control}>{control}</div>}
    </div>
  );
}

export function ValueLine({ children }: { children: ReactNode }) {
  return <div className={styles.value}>{children}</div>;
}

export function SectionSkeleton() {
  return (
    <div className={styles.loading}>
      <Spinner />
    </div>
  );
}

export function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<SectionSkeleton />}>{children}</Suspense>;
}
